/**
 * /workflows  — Workflow Template Management
 *
 * Two tabs:
 *   My Workflows   — org-scoped templates (CRUD + deactivate)
 *   Template Library — public templates (preview + clone)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Tab, Tabs, Paper,
  Grid, Card, CardContent, CardActions, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Tooltip, CircularProgress,
  Alert, Snackbar,
} from '@mui/material';
import {
  Plus, GitFork, Edit3, BarChart2, Layers, Copy,
  ToggleLeft, Eye, AlertCircle, CheckCircle,
} from 'lucide-react';
import axios from 'axios';
import BASE_URL from '../config';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { usePermissions } from '../contexts/PermissionContext';
import WorkflowBuilder from '../Components/Workflow/WorkflowBuilder';

// ─────────────────────────────────────────────────────────────────────────────
// WorkflowCard
// ─────────────────────────────────────────────────────────────────────────────
const ASSET_LABEL = { 1: 'Tree / Eco', 2: 'Lake' };

const WorkflowCard = ({ template, isPublic, onEdit, onDeactivate, onClone, onPreview }) => (
  <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
    <CardContent sx={{ flex: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="subtitle1" fontWeight={600} noWrap title={template.name}>
          {template.name}
        </Typography>
        {isPublic && <Chip label="Public" size="small" color="info" variant="outlined" />}
      </Box>
      {template.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {template.description}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        <Chip label={`${template.stage_count ?? '?'} stages`} size="small" icon={<Layers size={12} />} />
        {template.asset_type_id && <Chip label={ASSET_LABEL[template.asset_type_id] || 'Any'} size="small" />}
        {template.cloned_from && (
          <Chip label="Cloned" size="small" icon={<GitFork size={12} />} variant="outlined" />
        )}
      </Box>
    </CardContent>

    <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
      {isPublic ? (
        <>
          <Tooltip title="Preview"><IconButton size="small" onClick={() => onPreview(template)}><Eye size={16} /></IconButton></Tooltip>
          <Button size="small" startIcon={<Copy size={14} />} onClick={() => onClone(template)}>Use this template</Button>
        </>
      ) : (
        <>
          <Tooltip title="Edit"><IconButton size="small" onClick={() => onEdit(template)}><Edit3 size={16} /></IconButton></Tooltip>
          <Tooltip title="Clone to new workflow"><IconButton size="small" onClick={() => onClone(template)}><Copy size={14} /></IconButton></Tooltip>
          <Tooltip title="Deactivate"><IconButton size="small" color="warning" onClick={() => onDeactivate(template)}><ToggleLeft size={16} /></IconButton></Tooltip>
        </>
      )}
    </CardActions>
  </Card>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────────────────────
const WorkflowTemplates = () => {
  const { getToken } = useAuth();
  const { selectedOrgId } = useOrg();
  const { can } = usePermissions();

  const [tab, setTab] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  // Builder state
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);

  // Clone dialog state
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneSource, setCloneSource] = useState(null);
  const [cloneName, setCloneName] = useState('');
  const [cloning, setCloning] = useState(false);

  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  const fetchTemplates = useCallback(async () => {
    if (!selectedOrgId) return;
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) return;
      const res = await axios.get(`${BASE_URL}/api/workflow-templates`, {
        params: { organisation_id: selectedOrgId },
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates(res.data || []);
    } catch (err) {
      console.error(err);
      showSnack('Failed to load workflow templates', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedOrgId, getToken]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const myTemplates = templates.filter(t => t.organisation_id === selectedOrgId);
  const publicTemplates = templates.filter(t => !t.organisation_id);

  const handleEdit = (template) => {
    setEditingTemplateId(template.id);
    setBuilderOpen(true);
  };

  const handleCreate = () => {
    setEditingTemplateId(null);
    setBuilderOpen(true);
  };

  const handleDeactivate = async (template) => {
    if (!window.confirm(`Deactivate "${template.name}"? In-flight tasks are unaffected (they're snapshots).`)) return;
    try {
      const token = await getToken();
      if (!token) return;
      await axios.delete(`${BASE_URL}/api/workflow-templates/${template.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      showSnack(`"${template.name}" deactivated`);
      fetchTemplates();
    } catch (err) {
      showSnack('Failed to deactivate', 'error');
    }
  };

  const openCloneDialog = (template) => {
    setCloneSource(template);
    setCloneName(`${template.name} (Copy)`);
    setCloneDialogOpen(true);
  };

  const handleClone = async () => {
    if (!cloneName.trim()) return;
    setCloning(true);
    try {
      const token = await getToken();
      if (!token) return;
      const res = await axios.post(`${BASE_URL}/api/workflow-templates/${cloneSource.id}/clone`, {
        organisation_id: selectedOrgId,
        name: cloneName,
      }, { headers: { Authorization: `Bearer ${token}` } });
      showSnack(`Cloned as "${res.data.name}"`);
      setCloneDialogOpen(false);
      fetchTemplates();
      // Open builder on the clone
      setEditingTemplateId(res.data.id);
      setBuilderOpen(true);
    } catch (err) {
      showSnack('Failed to clone template', 'error');
    } finally {
      setCloning(false);
    }
  };

  const handlePreview = (template) => {
    setEditingTemplateId(template.id);
    setBuilderOpen(true);
  };

  const canRead   = can('Workflow', 'workflowTemplates', 'read');
  const canCreate = can('Workflow', 'workflowTemplates', 'create');
  const canEdit   = can('Workflow', 'workflowTemplates', 'update');
  const canDelete = can('Workflow', 'workflowTemplates', 'delete');

  if (!canRead) {
    return (
      <Box sx={{ p: 3 }}>
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Access denied
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You don't have permission to view workflows. Contact your organisation admin.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Page header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Workflows</Typography>
          <Typography variant="body2" color="text.secondary">
            Manage stage-based task workflows for your organisation.
          </Typography>
        </Box>
        {canCreate && (
          <Button variant="contained" startIcon={<Plus size={16} />} onClick={handleCreate}>
            New Workflow
          </Button>
        )}
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Tab label={`My Workflows (${myTemplates.length})`} />
        <Tab label={`Template Library (${publicTemplates.length})`} />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : tab === 0 ? (
        myTemplates.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
            <Layers size={40} color="#ccc" style={{ marginBottom: 8 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>No workflows yet</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Create a workflow from scratch, or clone from the Template Library tab.
            </Typography>
            {canCreate && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Button variant="contained" startIcon={<Plus size={14} />} onClick={handleCreate}>Start from blank</Button>
                <Button variant="outlined" onClick={() => setTab(1)}>Browse Template Library</Button>
              </Box>
            )}
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {myTemplates.map(t => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={t.id}>
                <WorkflowCard
                  template={t}
                  isPublic={false}
                  onEdit={canEdit ? handleEdit : () => {}}
                  onDeactivate={canDelete ? handleDeactivate : () => {}}
                  onClone={canCreate ? openCloneDialog : () => {}}
                  onPreview={handlePreview}
                />
              </Grid>
            ))}
          </Grid>
        )
      ) : (
        publicTemplates.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">No public templates available.</Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {publicTemplates.map(t => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={t.id}>
                <WorkflowCard
                  template={t}
                  isPublic
                  onEdit={() => {}}
                  onDeactivate={() => {}}
                  onClone={canCreate ? openCloneDialog : () => {}}
                  onPreview={handlePreview}
                />
              </Grid>
            ))}
          </Grid>
        )
      )}

      {/* Workflow Builder dialog */}
      <WorkflowBuilder
        open={builderOpen}
        templateId={editingTemplateId}
        organisationId={selectedOrgId}
        onClose={() => setBuilderOpen(false)}
        onSaved={() => { fetchTemplates(); showSnack('Workflow saved'); }}
      />

      {/* Clone dialog */}
      <Dialog open={cloneDialogOpen} onClose={() => setCloneDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Clone "{cloneSource?.name}"</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="New workflow name"
            value={cloneName}
            onChange={e => setCloneName(e.target.value)}
            fullWidth
            size="small"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloneDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleClone} disabled={cloning || !cloneName.trim()}
            startIcon={cloning ? <CircularProgress size={14} /> : <Copy size={14} />}>
            Clone &amp; Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkflowTemplates;
